import React, { useContext } from 'react';
import ReactApexChart from 'react-apexcharts';
import useReactApexChart from '../../hook/useReactApexChart';
import { FilterdData } from '../../helper/FilterdData';
import { ConfigContext } from '../../context/GlobalContext';
import DataNotFoundView from '../DataNotFoundView';
import { Languages } from "../../Utils/Languages";


const SaleReturnChart = () => {
    const { state } = useContext(ConfigContext);

    // Normalize date function to handle both seconds and milliseconds
    const getNormalizedDate = (dateStr) => {
        if (!dateStr) return new Date(0);
        const dateNum = parseInt(dateStr);
        return new Date(dateStr.toString().length > 10 ? dateNum : dateNum * 1000);
    };

    let saleReturnData = FilterdData(state, state?.SALERETURN || [], "date") || [];

    const getChartData = () => {
        let categories = [];
        let series = [];
        const filter = state?.ACTIVEFILTER || "All";

        if (filter === "Today" || filter === "Yeasterday") {
            // Hourly View (24 Hours)
            categories = Array.from({ length: 24 }, (_, i) => {
                const hour = i % 12 || 12;
                const ampm = i < 12 ? 'am' : 'pm';
                return `${hour}${ampm}`;
            });
            series = new Array(24).fill(0);
            saleReturnData.forEach(item => {
                const hour = getNormalizedDate(item.date).getHours();
                series[hour] += parseFloat(item.totalAmount) || 0;
            });
        } 
        else if (filter === "Last 7 Days") {
            // Last 7 Days (Daily)
            for (let i = 6; i >= 0; i--) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                categories.push(d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' }));
                
                const dayTotal = saleReturnData.reduce((acc, item) => {
                    const itemDate = getNormalizedDate(item.date).toDateString();
                    return itemDate === d.toDateString() ? acc + (parseFloat(item.totalAmount) || 0) : acc;
                }, 0);
                series.push(dayTotal);
            }
        } 
        else if (filter === "This Month" || filter === "Last Month") {
            // Weekly View (4-5 Weeks)
            categories = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"];
            series = new Array(5).fill(0);
            saleReturnData.forEach(item => {
                const date = getNormalizedDate(item.date).getDate();
                const weekIndex = Math.min(Math.floor((date - 1) / 7), 4);
                series[weekIndex] += parseFloat(item.totalAmount) || 0;
            });
        } 
        else if (filter.includes("Custome")) {
            // Custom Range (Dynamic)
            const start = parseInt(filter.split(",")[1]);
            const end = parseInt(filter.split(",")[2]);
            const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

            if (diffDays <= 2) {
                // Hourly if 1-2 days
                categories = Array.from({ length: 24 }, (_, i) => `${i % 12 || 12}${i < 12 ? 'am' : 'pm'}`);
                series = new Array(24).fill(0);
                saleReturnData.forEach(item => {
                    series[getNormalizedDate(item.date).getHours()] += parseFloat(item.totalAmount) || 0;
                });
            } else if (diffDays <= 10) {
                // Daily
                for (let i = 0; i <= diffDays; i++) {
                    const d = new Date(start + i * 86400000);
                    categories.push(d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' }));
                    const dayTotal = saleReturnData.reduce((acc, item) => {
                        return getNormalizedDate(item.date).toDateString() === d.toDateString() ? acc + (parseFloat(item.totalAmount) || 0) : acc;
                    }, 0);
                    series.push(dayTotal);
                }
            } else {
                // Weekly
                categories = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5+"];
                series = new Array(5).fill(0);
                saleReturnData.forEach(item => {
                    const date = getNormalizedDate(item.date);
                    const weekIdx = Math.min(Math.floor((date.getTime() - start) / (7 * 86400000)), 4);
                    series[weekIdx] += parseFloat(item.totalAmount) || 0;
                });
            }
        }
        else {
            // Default: All (Daily Sun-Sat or similar)
            categories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            series = new Array(7).fill(0);
            saleReturnData.forEach((info) => {
                const dayIndex = getNormalizedDate(info.date).getDay();
                series[dayIndex] += parseFloat(info.totalAmount) || 0;
            });
        }

        return { categories, series };
    };

    const { categories, series } = getChartData();
    const hasData = series.some(value => value > 0);

    const chartOptions = {
        chart: {
            height: 350,
            type: 'polarArea',
            toolbar: { show: false }
        },
        labels: categories,
        fill: { opacity: 0.85 },
        stroke: { width: 1, colors: ['#fff'] },
        colors: ['#487FFF', '#FF9F29', '#45B369', '#EF4444', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#6366F1'],
        yaxis: { show: false },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '12px',
            markers: { radius: 12 }
        },
        plotOptions: {
            polarArea: {
                rings: { strokeWidth: 1, strokeColor: '#e2e8f0' },
                spokes: { strokeWidth: 1, connectorColors: '#e2e8f0' }
            }
        },
        tooltip: {
            y: { formatter: (val) => `${state.SYMBOL}${val.toFixed(2)}` }
        }
    };

    return (
        <div className="col-12">
            <div className="card h-100 shadow-sm border-0">
                <div className="card-body p-24">
                    <div className="d-flex align-items-center justify-content-between mb-24">
                        <h6 className="mb-0 text-lg fw-bold">{Languages[state?.LANGUAGE]?.total_salereturn}</h6>
                    </div>
                    
                    <div className="mt-4 d-flex justify-content-center align-items-center" style={{ minHeight: '320px' }}>
                        {hasData ? (
                            <ReactApexChart options={chartOptions} series={series} type="polarArea" height={380} />
                        ) : (
                            <DataNotFoundView />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SaleReturnChart;
